import React, { useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import LpCheckbox from "../../../components/common/linchpin/LpCheckbox";
import "./ServiceOptionsV2.css";

function ServiceOptionsV2({
  location = {},
  typeOfLoc,
  labelCode,
  orderTypeCode,
  disabled = false,
  onChangeDirect,
  onChangeNested,
  // Optional override for the Expedite enable rule. Defaults to the
  // appointment-date derivation; callers (e.g. MultistopOrder) that own a
  // pre-existing `disableExpedite` state pass it here to keep legacy parity.
  expediteDisabled: expediteDisabledProp,
  // Org-gated (show_appointment_checkbox_in_create_edit_order_screen): shows the
  // "Appointment" flag checkbox. Mirrors the legacy ContextualModal checkbox —
  // toggles location.appointment.has_appointment (creates/removes the
  // APPOINTMENT_FEE accessorial backend-side). Mutually exclusive with Expedite.
  showAppointment = false,
  // Appointment-mandatory org (isAppointmentMandatoryOrg). Only such orgs make
  // Appointment/Expedite mutually exclusive — matching the pre-refactor flow.
  isOrgSpecific = false,
}) {
  const isPLH = orderTypeCode === "PLH";
  const isLHD = orderTypeCode === "LHD";
  const isPLHD = orderTypeCode === "PLHD" || orderTypeCode === "APAD";
  const isLH = orderTypeCode === "LH" || orderTypeCode === "NLH";
  const baseline = !isPLH && !isLHD && !isPLHD;

  const isPickup = typeOfLoc === "PICKUP";
  const isDelivery = typeOfLoc === "DELIVERY";

  const showSpecial =
    !isLH &&
    ((isPickup && (isPLH || isPLHD || baseline)) ||
      (isDelivery && (isLHD || isPLHD || baseline)));

  const showForm = ["R", "D"].includes(labelCode);

  const specialField = isPickup ? "pickup_special" : "delivery_special";
  const specialVal = !!location[specialField];
  const expediteVal = !!_.get(location, "appointment.has_expedite");
  const appointmentVal = !!_.get(location, "appointment.has_appointment");
  const signatureVal = !!location.is_signature_required;
  const formVal = !!location.is_form_required;

  // Match prod (ContextualModal / prod AddressLocationDetails): Expedite is blocked on
  // locked orders, or — when the "Show Appointment Checkbox" setting is ON — while
  // Appointment is checked (mutual exclusivity, keyed off the SETTING, not the org list).
  // The Appointment checkbox's own disable is org-gated (isOrgSpecific) below. Callers may
  // still override via expediteDisabledProp for legacy parity (e.g. MultistopOrder).
  const expediteDisabled =
    disabled ||
    (expediteDisabledProp !== undefined
      ? expediteDisabledProp
      : (showAppointment && appointmentVal));

  // Parent callbacks get fresh identity per render (AddressLocationDetails
  // doesn't memoize handleOnChangeDirect / handleOnChange). Stash in refs so
  // our per-checkbox handlers stay identity-stable and React.memo on
  // LpCheckbox actually skips re-renders.
  const onChangeDirectRef = useRef(onChangeDirect);
  const onChangeNestedRef = useRef(onChangeNested);
  useEffect(() => { onChangeDirectRef.current = onChangeDirect; });
  useEffect(() => { onChangeNestedRef.current = onChangeNested; });

  // Special's target field flips with leg type (pickup_special vs
  // delivery_special), so its handler depends on `specialField`. The other
  // three checkboxes write to fixed keys so they're empty-deps.
  const onSpecialChange = useCallback(
    (e) => onChangeDirectRef.current(specialField, e.target.checked),
    [specialField]
  );
  const onExpediteChange = useCallback((e) => {
    onChangeNestedRef.current("has_expedite", "appointment", e.target.checked);
  }, []);
  const onAppointmentChange = useCallback((e) => {
    onChangeNestedRef.current("has_appointment", "appointment", e.target.checked);
  }, []);
  const onSignatureChange = useCallback((e) => {
    onChangeDirectRef.current("is_signature_required", e.target.checked);
  }, []);
  const onFormChange = useCallback((e) => {
    onChangeDirectRef.current("is_form_required", e.target.checked);
  }, []);

  return (
    <div className="lp-v3 sov2-root">
      <div className="sov2-label">Service Options</div>
      <div className="sov2-options">
        {showAppointment && (
          <LpCheckbox
            label="Appointment"
            checked={appointmentVal}
            disabled={disabled || (isOrgSpecific && expediteVal)}
            onChange={onAppointmentChange}
          />
        )}
        {showSpecial && (
          <LpCheckbox
            label="Special"
            checked={specialVal}
            disabled={disabled}
            onChange={onSpecialChange}
          />
        )}
        <LpCheckbox
          label="Expedite"
          checked={expediteVal}
          disabled={expediteDisabled}
          onChange={onExpediteChange}
        />
        <LpCheckbox
          label="Signature Required"
          checked={signatureVal}
          disabled={disabled}
          onChange={onSignatureChange}
        />
        {showForm && (
          <LpCheckbox
            label="Form Required"
            checked={formVal}
            disabled={disabled}
            onChange={onFormChange}
          />
        )}
      </div>
    </div>
  );
}

export default React.memo(ServiceOptionsV2);
